import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { api, authStore } from '@/lib/api';
import { Heart, Lightning, Users, ArrowsClockwise, Clock, MapPin, Crown, Star } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  return `${h}h`;
};

/** Public, no-auth bubble spectator page. Shareable link. */
const PublicBubble = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const res = await axios.get(`${BACKEND_URL}/api/public/bubbles/${id}`);
    setData(res.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const follow = async () => {
    if (!authStore.token) {
      toast.message('Sign in to follow this bubble');
      window.location.href = `/login`;
      return;
    }
    setBusy(true);
    try {
      if (!following) {
        await api.post(`/bubbles/${id}/follow`);
        setFollowing(true);
        toast.success(`Now following ${data?.bubble?.name}`);
      } else {
        await api.delete(`/bubbles/${id}/follow`);
        setFollowing(false);
      }
      load();
    } catch (e) { toast.error('could not follow'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center text-white/65">loading bubble…</div>;
  const b = data.bubble;
  const alive = data.alive;
  const expiresMs = b.expires_at ? new Date(b.expires_at).getTime() - Date.now() : null;

  return (
    <div className="min-h-screen noise relative">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
            <span className="font-display text-lg font-black">BubbleX</span>
          </Link>
          <div className="text-[10px] uppercase tracking-widest text-white/60 hidden sm:block">public spectator</div>
          <Link to="/register" className="btn-volt px-4 py-2 rounded-full text-xs" data-testid="public-join">
            Join the network
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="relative aspect-square rounded-full overflow-hidden">
              <div className={`absolute inset-0 rounded-full blur-3xl opacity-40 bg-rar-${b.rarity}`} />
              {bubbleImageSrc(b) ? (
                <img src={bubbleImageSrc(b)} alt={b.name}
                     className={`relative h-full w-full object-cover rounded-full ${alive ? 'breathe' : ''}`}
                     style={!alive ? { filter: 'grayscale(0.6) brightness(0.85)' } : {}} />
              ) : (
                <div className={`relative h-full w-full rounded-full bg-rar-${b.rarity}`} />
              )}
              <div className="absolute inset-0 rounded-full ring-1 ring-white/15 pointer-events-none" />
            </motion.div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <div className="flex items-center flex-wrap gap-2">
              <span className={`chip rar-${b.rarity}`}>{b.rarity}</span>
              <span className="chip">{b.bubble_class}-class</span>
              <span className="chip">{b.trait}</span>
              {!alive && <span className="chip text-bx-magenta border-bx-magenta/40">memorial</span>}
              {b.parent_name && <span className="chip text-bx-gold border-bx-gold/40">heir of {b.parent_name}</span>}
            </div>
            <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter">
              <span className="shimmer-text">{b.name}</span>
            </h1>
            {b.personality_bio && (
              <p className="text-white/75 italic text-base sm:text-lg">"{b.personality_bio}"</p>
            )}
            {b.catchphrase && (
              <div className="text-bx-cyan font-bold text-sm">"{b.catchphrase}"</div>
            )}
            <div className="text-sm text-white/60">
              {alive ? (
                <>
                  Currently held by <span className="text-bx-magenta font-bold">@{b.current_holder || 'unknown'}</span>
                  {b.creator_username && <> · created by @{b.creator_username}</>}
                </>
              ) : (
                <>Lived {b.lifespan_days}d · created by @{b.creator_username}</>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={Users} label="Holders" value={b.unique_holders} color="text-bx-cyan" />
              <Stat icon={ArrowsClockwise} label="Transfers" value={b.transfers} color="text-bx-volt" />
              <Stat icon={Heart} label="Followers" value={b.followers} color="text-bx-magenta" />
              <Stat icon={alive ? Clock : Star} label={alive ? "Left" : "Tributes"}
                    value={alive ? (expiresMs ? fmt(expiresMs) : '∞') : (b.tributes || 0)} color="text-bx-gold" />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={follow}
                disabled={busy}
                data-testid="public-follow-btn"
                className={`px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 transition disabled:opacity-50 ${
                  following ? 'glass-hi text-white' : 'btn-primary'
                }`}
              >
                <Heart size={14} weight={following ? 'fill' : 'bold'} />
                {following ? 'Following' : 'Follow this bubble'}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Bubble link copied');
                }}
                data-testid="public-share-btn"
                className="px-5 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10"
              >
                Share
              </button>
              {!alive ? null : (
                <Link to={`/bubble/${b.id}`} className="px-5 py-3 rounded-full glass-hi text-sm font-bold hover:bg-white/10" data-testid="public-detail">
                  Full details →
                </Link>
              )}
            </div>

            {b.parent_name && (
              <div className="grad-border p-4">
                <div className="text-[10px] uppercase tracking-widest text-bx-gold flex items-center gap-1">
                  <Crown size={12} weight="fill" /> Bloodline
                </div>
                <div className="text-sm text-white/80 mt-1">
                  Born from <Link to={`/b/${b.parent_id}`} className="text-bx-cyan font-bold">{b.parent_name}</Link>.
                </div>
              </div>
            )}
            {b.child_name && (
              <div className="grad-border p-4">
                <div className="text-[10px] uppercase tracking-widest text-bx-volt flex items-center gap-1">
                  <Lightning size={12} weight="fill" /> Living legacy
                </div>
                <div className="text-sm text-white/80 mt-1">
                  Their pulse continues in <Link to={`/b/${b.child_id}`} className="text-bx-cyan font-bold">{b.child_name}</Link>.
                </div>
              </div>
            )}
          </div>
        </div>

        {b.journey && b.journey.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
              <MapPin size={20} weight="duotone" className="text-bx-cyan" /> Journey
            </h2>
            <div className="glass rounded-2xl p-5">
              {b.journey.slice().reverse().map((j, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
                  <span className="h-2 w-2 rounded-full bg-bx-magenta" />
                  <div className="text-sm flex-1">
                    <span className="text-bx-cyan">@{j.from}</span>
                    <span className="text-white/60 mx-2">→</span>
                    <span className="text-bx-magenta">@{j.to}</span>
                    <span className="text-white/60 text-xs ml-2">· {j.method}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/55">
                    {new Date(j.ts).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-xl p-4">
    <Icon size={14} weight="duotone" className={color} />
    <div className={`font-display text-xl font-black mt-1 ${color}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">{label}</div>
  </div>
);

export default PublicBubble;
